/* eslint-disable no-magic-numbers */
import React, { ChangeEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Box from 'carbon-react/lib/components/box';
import Typography from 'carbon-react/lib/components/typography';
import Button from 'carbon-react/lib/components/button';
import Link from 'carbon-react/lib/components/link';
import { CustomSelectChangeEvent, FilterableSelect, Option } from 'carbon-react/lib/components/select';

import { Countries } from '@futrli/localisation';

import styles from './excel-modal-content.scss';
import excelLogo from '../update/img/Excel Logo.png';

interface CreateFromExcelModalContentProps {
  countryCode?: string;
  onChangeCountryCode?: (code: string) => void;
}

const supportedCountryCodes = ['AU', 'CA', 'GB', 'FR', 'NZ', 'US'];

export const CreateFromExcelModalContent = ({
  countryCode,
  onChangeCountryCode
}: CreateFromExcelModalContentProps) => {
  const flags = useFlags();
  const intl = useIntl();
  // Carbon forwards native anchor attributes, although ButtonProps does not declare `download`.
  const downloadButtonProps = {
    download: intl.formatMessage({
      id: 'predict.org_template_xls',
      defaultMessage: 'Organisation Template.xls'
    })
  };
  const countryCodeList = Object.entries(Countries.getNames(intl)).filter(([code]) =>
    supportedCountryCodes.includes(code)
  );
  const startStep = flags.excelOrgCountryCode ? 2 : 1;

  const handleChangeCountryCode = (ev: CustomSelectChangeEvent | ChangeEvent<HTMLInputElement>) => {
    onChangeCountryCode?.(ev.target.value);
  };

  return (
    <Box className={styles.container}>
      <img src={excelLogo} alt="Excel" width={48} height={48} style={{ marginBottom: '16px' }} />
      {flags.excelOrgCountryCode && (
        <>
          <Typography fontSize="14px" fontWeight="500" mb={0}>
            <FormattedMessage id="predict.create_forecast.step_x" defaultMessage="Step {step}" values={{ step: 1 }} />{' '}
          </Typography>
          <Typography fontSize="14px" mb="8px" color="var(--colors-utility-yin-055, rgba(0, 0, 0, 0.55))">
            <span id="excel-modal-select-country-label">
              <FormattedMessage
                id="predict.select_country_or_region"
                defaultMessage="Select what country/region the organisation belongs in"
              />
            </span>
          </Typography>
          <FilterableSelect
            key="country"
            aria-labelledby="excel-modal-select-country-label"
            placeholder={intl.formatMessage({
              id: 'common.predict.please_select',
              defaultMessage: 'Please select...'
            })}
            value={countryCode ?? ''}
            onChange={handleChangeCountryCode}
            data-testid="excel-org-country-code"
            listMaxHeight={360}
            marginTop="10px"
            mb="16px"
          >
            {countryCodeList.map(([code, label]) => (
              <Option key={code} value={code} text={label} disabled={code === '-'} />
            ))}
          </FilterableSelect>
        </>
      )}

      <Typography fontSize="14px" fontWeight="500" mb={0}>
        <FormattedMessage
          id="predict.create_forecast.step_x"
          defaultMessage="Step {step}"
          values={{ step: startStep }}
        />
      </Typography>
      <Typography fontSize="14px" mb="8px" color="var(--colors-utility-yin-055, rgba(0, 0, 0, 0.55))">
        <FormattedMessage
          id="predict.download_excel_template_below"
          defaultMessage="Download the Excel template below."
        />
      </Typography>
      <Button
        buttonType="secondary"
        mb="16px"
        iconType="download"
        iconPosition="after"
        href={
          countryCode === 'FR'
            ? 'https://assets.futrli.com/predict/templates/organisation_import_template_fr.xlsx'
            : 'https://assets.futrli.com/predict/templates/organisation_import_template.xlsx'
        }
        {...downloadButtonProps}
      >
        <FormattedMessage id="predict.org_template_xls" defaultMessage="Organisation Template.xls" />
      </Button>
      <Typography fontSize="14px" fontWeight="500" mb={0}>
        <FormattedMessage
          id="predict.create_forecast.step_x"
          defaultMessage="Step {step}"
          values={{ step: startStep + 1 }}
        />
      </Typography>
      <Typography fontSize="14px" color="var(--colors-utility-yin-055, rgba(0, 0, 0, 0.55))" mb="16px">
        <FormattedMessage
          id="predict.update_data_following_guidelines"
          defaultMessage="Update data following the <link>import guidelines (opens in new tab)</link>."
          values={{
            link: chunks => (
              <Link
                href="https://support.futrli.com/en/articles/10485548-excel-organisations"
                target="_blank"
                rel="noreferrer"
              >
                <span style={{ fontWeight: 500 }}>{chunks}</span>
              </Link>
            )
          }}
        />
      </Typography>

      <Typography fontSize="14px" fontWeight="500" mb={0}>
        <FormattedMessage
          id="predict.create_forecast.step_x"
          defaultMessage="Step {step}"
          values={{ step: startStep + 2 }}
        />
      </Typography>
      <Typography fontSize="14px" mb={0} color="var(--colors-utility-yin-055, rgba(0, 0, 0, 0.55))">
        <FormattedMessage
          id="predict.upload_data_file_max_size_25.step_3"
          defaultMessage="Upload the updated file. Max file size is 25mb."
        />
      </Typography>
    </Box>
  );
};
